import { FocusModeAPI } from '@business/api/focus_mode';
import {
  FocusProspectsResponse,
  FocusQueueItem,
  FocusQueueItemType,
  FocusQueueRep,
  FocusQueueResponse,
  FocusSessionResponse,
  FocusSnoozeItemType,
} from '@business/entities/focusMode';
import { UserId } from '@business/entities/account';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';

export class FocusModeBloc extends BaseBloc {
  private readonly queueSubject = new BehaviorSubject<FocusQueueItem[]>([]);
  private readonly exhaustedSubject = new BehaviorSubject<boolean>(false);
  private readonly repsSubject = new BehaviorSubject<FocusQueueRep[]>([]);
  private readonly prospectsSubject = new BehaviorSubject<FocusProspectsResponse | null>(null);
  private readonly sessionSubject = new BehaviorSubject<FocusSessionResponse | null>(null);
  private readonly workloadSubject = new BehaviorSubject<number>(0);

  readonly queue$ = this.queueSubject.asObservable();
  /** True once the urgent queue is empty — drives the Prospecting Mode transition. */
  readonly exhausted$ = this.exhaustedSubject.asObservable();
  /** Reps an administrator may look in on. Empty for everyone else. */
  readonly reps$ = this.repsSubject.asObservable();
  readonly prospects$ = this.prospectsSubject.asObservable();
  readonly session$ = this.sessionSubject.asObservable();
  /** Estimated minutes of work sitting in the queue (AC#4). */
  readonly workload$ = this.workloadSubject.asObservable();

  onReset() {
    this.queueSubject.next([]);
    this.exhaustedSubject.next(false);
    this.repsSubject.next([]);
    this.prospectsSubject.next(null);
    this.sessionSubject.next(null);
    this.workloadSubject.next(0);
  }

  get queue() {
    return this.queueSubject.value;
  }

  /**
   * `viewAs` builds another rep's queue instead of the caller's — administrator-only,
   * and enforced server-side. Read-only: the snooze and complete calls below never
   * carry it, so an item can only ever be worked by the rep it belongs to.
   */
  fetchQueue = (fresh = false, viewAs?: UserId) => {
    return FocusModeAPI.fetchQueue(fresh, viewAs).pipe(
      tap((response: FocusQueueResponse) => {
        this.queueSubject.next(response.items);
        this.exhaustedSubject.next(response.exhausted);
        this.workloadSubject.next(response.totalEstimatedMinutes ?? 0);
      }),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  fetchReps = () => {
    return FocusModeAPI.fetchReps().pipe(
      tap(r => this.repsSubject.next(r.reps ?? [])),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  fetchProspects = (viewAs?: UserId) => {
    return FocusModeAPI.fetchProspects(viewAs).pipe(
      tap((response: FocusProspectsResponse) => this.prospectsSubject.next(response)),
      handleMessage({ error: requestMessage('fetch_general_error') }),
    );
  };

  startSession = () => {
    return FocusModeAPI.startSession().pipe(tap(r => this.sessionSubject.next(r.session)));
  };

  heartbeat = (actionsTaken = 0) => {
    return FocusModeAPI.heartbeat(actionsTaken).pipe(tap(r => this.sessionSubject.next(r.session)));
  };

  endSession = () => {
    return FocusModeAPI.endSession().pipe(tap(r => this.sessionSubject.next(r.session)));
  };

  /**
   * Removes the item locally as well as server-side. The server queue is cached for
   * 60s, so waiting for a refetch would leave the rep staring at an item they just
   * dealt with.
   */
  snooze = (itemType: FocusSnoozeItemType, itemId: string, minutes: number) => {
    return FocusModeAPI.snoozeItem(itemType, itemId, minutes).pipe(
      tap(r => {
        this.dropItem(itemType, itemId);
        this.sessionSubject.next(r.session);
      }),
      handleMessage({ type: requestMessage('focus_snooze') }),
    );
  };

  complete = (itemType: FocusSnoozeItemType, itemId: string, actionsTaken = 0) => {
    return FocusModeAPI.completeItem(itemType, itemId, actionsTaken).pipe(
      tap(r => {
        this.dropItem(itemType, itemId);
        this.sessionSubject.next(r.session);
      }),
      handleMessage({ type: requestMessage('focus_complete') }),
    );
  };

  /**
   * Reflect a just-logged activity on the item without disturbing the queue.
   *
   * The item deliberately stays where it is — a rep often logs a call and then a note on
   * the same record, and re-ranking mid-work would move it out from under them. What
   * cannot stay is the reason text: the moment an activity is logged, "No activity for
   * 197 days" and "New lead, never contacted" are false, and those badges are exactly
   * what the rep reads to decide what is urgent. They are retired here; the next full
   * queue build re-scores and re-orders properly.
   */
  markActivityLogged = (itemType: FocusQueueItemType, itemId: string) => {
    const stale = ['no_recent_activity', 'new_uncontacted_lead'];

    this.queueSubject.next(
      this.queueSubject.value.map(item =>
        item.type === itemType && item.id === itemId
          ? {
              ...item,
              daysSinceActivity: 0,
              isUncontacted: false,
              reasons: item.reasons.filter(reason => !stale.includes(reason.key)),
            }
          : item,
      ),
    );
  };

  /**
   * Prospect Mode's optimistic removal, the counterpart of dropItem() for the queue: an
   * account the rep has just snoozed or finished with leaves the sidebar immediately
   * rather than after a refetch. A section left with no accounts is dropped too, so the
   * rep is not left staring at an empty filter-set heading.
   */
  dropProspect = (orgId: string) => {
    const current = this.prospectsSubject.value;
    if (!current) return;

    const sections = current.sections
      .map(section => ({ ...section, organizations: section.organizations.filter(o => o.id !== orgId) }))
      .filter(section => section.organizations.length > 0);

    this.prospectsSubject.next({ ...current, sections });
  };

  private dropItem(itemType: FocusSnoozeItemType, itemId: string) {
    const remaining = this.queueSubject.value.filter(i => !(i.type === itemType && i.id === itemId));
    this.queueSubject.next(remaining);
    // Recomputed locally so the remaining workload drops as the rep works through the
    // queue, rather than staying stale until the next fetch.
    this.workloadSubject.next(remaining.reduce((total, item) => total + (item.estimatedMinutes ?? 0), 0));
    if (remaining.length === 0) this.exhaustedSubject.next(true);
  }
}
